import { memo, useEffect, useRef, useState } from 'react';
// @ts-ignore
import BackgroundVideo from 'react-background-video-player';
import { StaticImageData } from 'next/image';
import classnames from 'classnames';

import styles from './Hero.module.scss';

import BaseImage from '@/components/BaseImage/BaseImage';
import Cta from '@/components/Cta/Cta';

import useLayout from '@/hooks/use-layout';
import useReducedMotion from '@/hooks/use-reduced-motion';

export type Props = {
  className?: string;
  title: string;
  subTitle: string;
  image: {
    data: StaticImageData;
    tabletData: StaticImageData;
    desktopData: StaticImageData;
    alt: string;
  };
  video: {
    src: string;
    tabletSrc: string;
    desktopSrc: string;
  };
  cta: {
    href: string;
    text: string;
  };
};

function Hero({ className, title, subTitle, image, cta, video }: Props) {
  const [finalImageSrc, setFinalImageSrc] = useState<StaticImageData>(image.desktopData);
  const [finalVideoSrc] = useState<string | undefined>(video.desktopSrc);
  const { layout } = useLayout();
  const reducedMotion = useReducedMotion();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const VideoRef = useRef<any | null>();

  useEffect(() => {
    if (layout.mobile) {
      setFinalImageSrc(image.data);
    } else if (layout.tablet) {
      setFinalImageSrc(image.tabletData);
    } else {
      setFinalImageSrc(image.desktopData);
    }

    VideoRef.current?.play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  return (
    <section className={classnames(styles.Hero, className)}>
      <div className={styles.content}>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.line} />
        <div className={styles.wrapper}>
          <p className={styles.subtitle}>{subTitle}</p>
          <Cta className={styles.cta} title={cta.text} href={cta.href} />
        </div>
      </div>

      <div className={styles.mediaContainer}>
        {finalVideoSrc && !reducedMotion ? (
          <BackgroundVideo
            className={styles.video}
            src={finalVideoSrc}
            autoPlay={true}
            muted={true}
            loop={true}
            disableBackgroundCover={true}
            preload={'auto'}
            playsInline={true}
            tabIndex={null}
            extraVideoElementProps={{ crossOrigin: 'anonymous' }}
            ref={VideoRef}
          />
        ) : (
          <BaseImage className={styles.image} data={finalImageSrc} alt={image.alt} loading="eager" />
        )}
      </div>
    </section>
  );
}

export default memo(Hero);
